import { useState, useEffect, useCallback } from "react";
import strings from "../../../locales/ro.json";
import { useApi } from "../../../hooks/useApi";

export default function usePermissionsManager() {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolePermissionsMap, setRolePermissionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { api, authToken } = useApi();

  const clearError = () => setError(null);

  // ─── Fetch all data ────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [permsRes, rolesRes, matrixRes] = await Promise.all([
        api("permissions"),
        api("roles"),
        api("permissions/matrix"),
      ]);

      if (Array.isArray(permsRes)) setPermissions(permsRes);
      if (Array.isArray(rolesRes)) setRoles(rolesRes);

      // Build a map: { roleId: [permissionId, ...] }
      if (Array.isArray(matrixRes)) {
        const map = {};
        matrixRes.forEach(({ role_id, permission_id }) => {
          if (!map[role_id]) map[role_id] = [];
          map[role_id].push(permission_id);
        });
        setRolePermissionsMap(map);
      }
    } catch (err) {
      console.error("Error fetching permissions data:", err);
      setError(strings.admin.permissions.errors.fetch);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (authToken) fetchAll();
  }, [authToken, fetchAll]);

  // ─── Permission CRUD ───────────────────────────────────

  const createPermission = async ({ slug, label, description }) => {
    setError(null);
    try {
      await api("permissions", "POST", { slug, label, description });
      await fetchAll();
    } catch (err) {
      console.error("Error creating permission:", err);
      setError(strings.admin.permissions.errors.create);
      throw err;
    }
  };

  const deletePermission = async (id) => {
    setError(null);
    try {
      await api(`permissions/${id}`, "DELETE");
      setPermissions((prev) => prev.filter((p) => p.id !== id));
      // Also clean up the rolePermissionsMap
      setRolePermissionsMap((prev) => {
        const newMap = { ...prev };
        Object.keys(newMap).forEach((roleId) => {
          newMap[roleId] = newMap[roleId].filter((pid) => pid !== id);
        });
        return newMap;
      });
    } catch (err) {
      console.error("Error deleting permission:", err);
      setError(strings.admin.permissions.errors.delete);
      throw err;
    }
  };

  // ─── Role Permission Assignment ────────────────────────

  const setRolePermissions = async (roleId, permissionIds) => {
    setError(null);
    try {
      await api(`permissions/role/${roleId}`, "PUT", { permission_ids: permissionIds });
      setRolePermissionsMap((prev) => ({
        ...prev,
        [roleId]: permissionIds,
      }));
    } catch (err) {
      console.error("Error setting role permissions:", err);
      setError(strings.admin.permissions.errors.save);
      throw err;
    }
  };

  const toggleRolePermission = async (roleId, permissionId) => {
    const current = rolePermissionsMap[roleId] || [];
    let updated;
    if (current.includes(permissionId)) {
      updated = current.filter((id) => id !== permissionId);
    } else {
      updated = [...current, permissionId];
    }
    await setRolePermissions(roleId, updated);
  };

  return {
    permissions,
    roles,
    rolePermissionsMap,
    loading,
    error,
    clearError,
    fetchAll,
    createPermission,
    deletePermission,
    setRolePermissions,
    toggleRolePermission,
  };
}
